import type { ListAnnouncementsQuery } from '@clockwise/clients/announcements';
import type { ListApprovalPoliciesQuery } from '@clockwise/clients/approval-policies';
import type { ListAuditQuery } from '@clockwise/clients/audit';
import type { ListCustomFieldsQuery } from '@clockwise/clients/custom-fields';
import type { ListCustomersQuery } from '@clockwise/clients/customers';
import type { ListDashboardsQuery } from '@clockwise/clients/dashboards';
import type { ListDepartmentsQuery } from '@clockwise/clients/departments';
import type { ListExpenseCatalogsQuery } from '@clockwise/clients/expense-catalogs';
import type { ListHolidaysQuery } from '@clockwise/clients/holidays';
import type {
  GenerationRunInput,
  InvoiceBulkTarget,
  InvoiceListQuery,
  InvoiceRunItemsQuery,
  InvoiceRunListQuery,
} from '@clockwise/clients/invoices';
import type { ListNotificationsQuery } from '@clockwise/clients/notifications';
import type { ListProductsQuery } from '@clockwise/clients/products';
import type { ListRatecardsQuery } from '@clockwise/clients/ratecards';
import type { ListReleaseNotesQuery } from '@clockwise/clients/release-notes';
import type { ListSavedReportsQuery } from '@clockwise/clients/report-definitions';
import type { TimesheetReportQuery } from '@clockwise/clients/reports';
import type { ListServiceRolesQuery } from '@clockwise/clients/service-roles';
import type { ListSkillSubmissionsQuery } from '@clockwise/clients/skill-submissions';
import type { ListSkillsQuery } from '@clockwise/clients/skills';
import type { ListSowsQuery } from '@clockwise/clients/sows';
import type { ListSystemRolesQuery } from '@clockwise/clients/system-roles';
import type { TimerStatus } from '@clockwise/clients/timers';
import type { ListTimesheetUnlocksQuery } from '@clockwise/clients/timesheet-unlocks';
import type { ListTimesheetsQuery, RangeQuery } from '@clockwise/clients/timesheets';
import type { ListSrControllerCandidatesQuery, ListUsersQuery } from '@clockwise/clients/users';
import type { ReportSpec } from '@clockwise/semantic-core';

export const queryKeys = {
  announcements: {
    all: ['announcements'] as const,
    list: (q: ListAnnouncementsQuery) => [...queryKeys.announcements.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.announcements.all, 'detail', id] as const,
    // Separate key (and permission, and refetch policy) from the admin list
    // above — this is the all-users "what's live right now" banner feed.
    live: () => [...queryKeys.announcements.all, 'live'] as const,
  },
  releaseNotes: {
    all: ['releaseNotes'] as const,
    list: (q: ListReleaseNotesQuery) => [...queryKeys.releaseNotes.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.releaseNotes.all, 'detail', id] as const,
  },
  tenants: {
    all: ['tenants'] as const,
    list: () => [...queryKeys.tenants.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.tenants.all, 'detail', id] as const,
  },
  company: {
    all: ['company'] as const,
    info: () => [...queryKeys.company.all, 'info'] as const,
  },
  products: {
    all: ['products'] as const,
    list: (q: ListProductsQuery) => [...queryKeys.products.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.products.all, 'detail', id] as const,
    byIds: (ids: readonly string[]) =>
      [...queryKeys.products.all, 'byIds', [...ids].sort()] as const,
  },
  holidays: {
    all: ['holidays'] as const,
    list: (q: ListHolidaysQuery) => [...queryKeys.holidays.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.holidays.all, 'detail', id] as const,
    /** Distinct countries present in the tenant's calendar — invalidated by
     *  the same `holidays.all` prefix as the list, since adding the first
     *  holiday for a country changes it. */
    countries: () => [...queryKeys.holidays.all, 'countries'] as const,
  },
  customers: {
    all: ['customers'] as const,
    list: (q: ListCustomersQuery) => [...queryKeys.customers.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.customers.all, 'detail', id] as const,
    subsidiaries: (id: string, page = 0) =>
      [...queryKeys.customers.all, 'subsidiaries', id, page] as const,
    versions: (id: string) => [...queryKeys.customers.all, 'versions', id] as const,
    byIds: (ids: readonly string[]) =>
      [...queryKeys.customers.all, 'byIds', [...ids].sort()] as const,
    // Distinct from `byIds`: the same id set returns only active customers
    // here, so it must not share a cache entry with the all-statuses fetch.
    activeByIds: (ids: readonly string[]) =>
      [...queryKeys.customers.all, 'activeByIds', [...ids].sort()] as const,
  },
  customFields: {
    all: ['customFields'] as const,
    list: (q: ListCustomFieldsQuery) => [...queryKeys.customFields.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.customFields.all, 'detail', id] as const,
    /** Single tenant-wide cache for the editor/viewer/filter hook — replaces
     *  per-object keys to collapse N requests into one per session. */
    byTenant: () => [...queryKeys.customFields.all, 'byTenant'] as const,
  },
  customerBudgets: {
    all: ['customerBudgets'] as const,
    // `asOf` distinguishes the in-effect snapshot from the full history; the
    // tab toggles between them, so they must not share a cache entry.
    list: (customerId: string, params?: { asOf?: string; limit?: number; offset?: number }) =>
      [
        ...queryKeys.customerBudgets.all,
        'list',
        customerId,
        params?.asOf ?? 'all',
        params?.limit ?? null,
        params?.offset ?? 0,
      ] as const,
    detail: (id: string) => [...queryKeys.customerBudgets.all, 'detail', id] as const,
    /** The form picker's engagement choices for one customer. */
    engagementOptions: (customerId: string) =>
      [...queryKeys.customerBudgets.all, 'engagement-options', customerId] as const,
    // Keyed on the budget ids on screen — the actuals read answers for exactly
    // the rows a page is rendering, so a different page is a different entry.
    actuals: (customerId: string, budgetIds: readonly string[], periods: number, today: string) =>
      [
        ...queryKeys.customerBudgets.all,
        'actuals',
        customerId,
        [...budgetIds].sort().join(','),
        periods,
        today,
      ] as const,
    /**
     * The Budgets tab's Budget vs Actuals view, which runs a REPORT SPEC
     * against `/api/analytics/query` rather than a budgets endpoint.
     *
     * Keyed on the whole spec, because the spec IS the question: change the
     * period and it is a different answer, and react-query's structural
     * hashing turns the object into a stable key without anything here having
     * to name the fields that matter. `null` before a period resolves, when
     * the query is disabled and the entry is never filled.
     */
    actualsReport: (customerId: string, spec: unknown) =>
      [...queryKeys.customerBudgets.all, 'actuals-report', customerId, spec ?? null] as const,
  },
  customerExpenses: {
    all: ['customerExpenses'] as const,
    // `asOf` distinguishes the in-force snapshot from the full history; the
    // Invoicing tab toggles between them, so they must not share a cache entry.
    list: (customerId: string, params?: { asOf?: string; limit?: number; offset?: number }) =>
      [
        ...queryKeys.customerExpenses.all,
        'list',
        customerId,
        params?.asOf ?? 'all',
        params?.limit ?? null,
        params?.offset ?? 0,
      ] as const,
    detail: (id: string) => [...queryKeys.customerExpenses.all, 'detail', id] as const,
  },
  variableExpenseOccurrences: {
    all: ['variableExpenseOccurrences'] as const,
    // Keyed on the PERIOD, not just the month: the grid and both downloads all
    // speak in the half-open [start, end) window an invoice stores, and two
    // different windows are two different answers.
    grid: (params: {
      periodStart: string;
      periodEnd: string;
      customerId?: string;
      expenseCatalogId?: string;
      /** Part of the key: the two tabs are two different answers for one
       *  period, and sharing a cache entry would show one tab the other's
       *  rows. */
      state?: 'all' | 'needs';
      offset?: number;
    }) =>
      [
        ...queryKeys.variableExpenseOccurrences.all,
        'grid',
        params.periodStart,
        params.periodEnd,
        params.customerId ?? 'all',
        params.expenseCatalogId ?? 'all',
        // `state` was documented as part of the key but was never actually in
        // it, so the two tabs shared one cache entry and each showed the
        // other's rows until its own refetch landed.
        params.state ?? 'all',
        params.offset ?? 0,
      ] as const,
    forLine: (customerExpenseId: string) =>
      [...queryKeys.variableExpenseOccurrences.all, 'line', customerExpenseId] as const,
  },
  customerTeam: {
    all: ['customerTeam'] as const,
    inEffect: (
      customerId: string,
      params?: { asOf?: string; q?: string; limit?: number; offset?: number },
    ) =>
      [
        ...queryKeys.customerTeam.all,
        'inEffect',
        customerId,
        params?.asOf ?? 'today',
        params?.q ?? '',
        params?.limit ?? null,
        params?.offset ?? 0,
      ] as const,
    listAll: (customerId: string, params?: { q?: string; limit?: number; offset?: number }) =>
      [
        ...queryKeys.customerTeam.all,
        'all',
        customerId,
        params?.q ?? '',
        params?.limit ?? null,
        params?.offset ?? 0,
      ] as const,
    detail: (id: string) => [...queryKeys.customerTeam.all, 'detail', id] as const,
    // `withLabels` distinguishes the enriched (user-detail section) fetch from
    // the bare picker fetch so they don't share a cache entry.
    forUser: (q: { teamUserId: string; fromDate: string; toDate: string; withLabels?: boolean }) =>
      [...queryKeys.customerTeam.all, 'forUser', q] as const,
  },
  customerContacts: {
    all: ['customerContacts'] as const,
    list: (customerId: string) => [...queryKeys.customerContacts.all, 'list', customerId] as const,
    detail: (id: string) => [...queryKeys.customerContacts.all, 'detail', id] as const,
  },
  customerActivities: {
    all: ['customerActivities'] as const,
    list: (customerId: string) =>
      [...queryKeys.customerActivities.all, 'list', customerId] as const,
    detail: (customerId: string, id: string) =>
      [...queryKeys.customerActivities.all, 'detail', customerId, id] as const,
  },
  ratecards: {
    all: ['ratecards'] as const,
    list: (q: ListRatecardsQuery) => [...queryKeys.ratecards.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.ratecards.all, 'detail', id] as const,
    active: (asOf?: string) => [...queryKeys.ratecards.all, 'active', asOf ?? null] as const,
  },
  sows: {
    all: ['sows'] as const,
    list: (q: ListSowsQuery) => [...queryKeys.sows.all, 'list', q] as const,
    detail: (id: string, asOf?: string) =>
      [...queryKeys.sows.all, 'detail', id, asOf ?? null] as const,
    versions: (id: string) => [...queryKeys.sows.all, 'versions', id] as const,
  },
  businessUnits: {
    all: ['businessUnits'] as const,
    list: () => [...queryKeys.businessUnits.all, 'list'] as const,
    names: () => [...queryKeys.businessUnits.all, 'names'] as const,
    detail: (id: string) => [...queryKeys.businessUnits.all, 'detail', id] as const,
    summary: (id: string) => [...queryKeys.businessUnits.all, 'summary', id] as const,
    roles: (id: string) => [...queryKeys.businessUnits.all, 'roles', id] as const,
    ofUser: (userId: string) => [...queryKeys.businessUnits.all, 'ofUser', userId] as const,
  },
  systemRoles: {
    all: ['systemRoles'] as const,
    list: (q: ListSystemRolesQuery) => [...queryKeys.systemRoles.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.systemRoles.all, 'detail', id] as const,
    cloneDraft: (id: string) => [...queryKeys.systemRoles.all, 'clone-draft', id] as const,
  },
  serviceRoles: {
    all: ['serviceRoles'] as const,
    list: (q: ListServiceRolesQuery) => [...queryKeys.serviceRoles.all, 'list', q] as const,
    byIds: (ids: readonly string[]) =>
      [...queryKeys.serviceRoles.all, 'byIds', [...ids].sort()] as const,
    detail: (id: string) => [...queryKeys.serviceRoles.all, 'detail', id] as const,
  },
  chartOfAccounts: {
    all: ['chartOfAccounts'] as const,
  },
  departments: {
    all: ['departments'] as const,
    list: (q: ListDepartmentsQuery) => [...queryKeys.departments.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.departments.all, 'detail', id] as const,
  },
  expenseCatalogs: {
    all: ['expense-catalogs'] as const,
    list: (q: ListExpenseCatalogsQuery) => [...queryKeys.expenseCatalogs.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.expenseCatalogs.all, 'detail', id] as const,
  },
  /** Tenant invoice settings — a singleton, so one key with no arguments.
   *  Invalidated alongside `invoices.all` on save: the issuer block and the
   *  defaults feed `invoices.newDefaults`. */
  invoiceSettings: {
    all: ['invoiceSettings'] as const,
    current: () => [...queryKeys.invoiceSettings.all, 'current'] as const,
  },
  invoices: {
    all: ['invoices'] as const,
    list: (q: InvoiceListQuery) => [...queryKeys.invoices.all, 'list', q] as const,
    /** Ids matching a filter — the bulk-approve "select all matching" read.
     *  Keyed separately from `list` so paging the table doesn't evict it. */
    ids: (q: Omit<InvoiceListQuery, 'limit' | 'offset'>) =>
      [...queryKeys.invoices.all, 'ids', q] as const,
    /** How many matching invoices sit in each status — what the bulk bar
     *  quotes before it acts. Keyed without paging for the same reason as
     *  `ids`: the counts describe the filter, not the page. */
    statusCounts: (q: Omit<InvoiceListQuery, 'limit' | 'offset'>) =>
      [...queryKeys.invoices.all, 'status-counts', q] as const,
    detail: (id: string) => [...queryKeys.invoices.all, 'detail', id] as const,
    /** A dry run of the bulk invoice-date change: what it WOULD do for this
     *  selection and date, read before anyone confirms. */
    invoiceDatePreview: (target: InvoiceBulkTarget, invoiceDate: string) =>
      [...queryKeys.invoices.all, 'invoice-date-preview', target, invoiceDate] as const,
    /** A dry run of the bulk send: which invoices would go, and which are
     *  skipped and why. */
    sendPreview: (target: InvoiceBulkTarget) =>
      [...queryKeys.invoices.all, 'send-preview', target] as const,
    /** Where a send would actually land (the redirect guard). Not under `all`:
     *  it is server config, not invoice data, and nothing an edit changes. */
    sendConfig: () => ['invoices-send-config'] as const,
    /** An invoice's attachments. Under `all`, so an invoice save refreshes
     *  them too. */
    attachments: (id: string) => [...queryKeys.invoices.all, 'attachments', id] as const,
    /** Review flags for a page of invoices. Keyed by the id SET, so paging or
     *  re-filtering fetches its own page's flags rather than reusing the last
     *  page's answer. */
    flags: (ids: readonly string[]) => [...queryKeys.invoices.all, 'flags', ids] as const,
    /** What a new invoice would carry — issuer, remittance, defaults, the
     *  expected number. Tenant-level, no customer involved, so one key with
     *  no arguments — it loads the instant the create screen opens. */
    newDefaults: () => [...queryKeys.invoices.all, 'new-defaults'] as const,
    /** The Bill To block a new invoice for this customer would snapshot right
     *  now. Keyed by customer, separately from `newDefaults`, since it's the
     *  only part of the create screen's preview that needs one. A sibling
     *  segment, not nested under `new-defaults`, so invalidating the tenant
     *  defaults never also refetches (and re-seeds) every Bill To block. */
    newBillTo: (customerId: string) =>
      [...queryKeys.invoices.all, 'new-bill-to', customerId] as const,
    /** The rendered preview. Separate from `detail` because it is re-fetched
     *  on a different rhythm — after every save, and never on mount alone —
     *  and because evicting a megabyte of PDF should not evict the JSON the
     *  form is bound to. `updatedAt` is in the key so a save produces a new
     *  key rather than relying on invalidation to beat the iframe's own load. */
    pdf: (id: string, updatedAt: string) =>
      [...queryKeys.invoices.all, 'pdf', id, updatedAt] as const,

    // ---- Generation runs --------------------------------------------------
    /** What a run WOULD do. Keyed by the whole input because that is exactly
     *  what it describes — change the month or a filter and it is a different
     *  answer, not a stale one. */
    runPreview: (input: GenerationRunInput) =>
      [...queryKeys.invoices.all, 'run-preview', input] as const,
    runs: (q: InvoiceRunListQuery) => [...queryKeys.invoices.all, 'runs', q] as const,
    run: (runId: string) => [...queryKeys.invoices.all, 'run', runId] as const,
    runItems: (runId: string, q: InvoiceRunItemsQuery) =>
      [...queryKeys.invoices.all, 'run', runId, 'items', q] as const,
    /** Counts only, polled every few seconds while a run is live. Kept apart
     *  from `run` so the toast's poll does not churn the detail page's cache. */
    runProgress: (runId: string) => [...queryKeys.invoices.all, 'run', runId, 'progress'] as const,
    /** Who has no invoice for a month, and why. Keyed on the MONTH and not on
     *  a run — the question is about a customer and a month, and which run
     *  looked at them is an implementation detail. */
    unbilled: (month: string) => [...queryKeys.invoices.all, 'unbilled', month] as const,
  },
  skills: {
    all: ['skills'] as const,
    list: (q: ListSkillsQuery) => [...queryKeys.skills.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.skills.all, 'detail', id] as const,
    categories: () => [...queryKeys.skills.all, 'categories'] as const,
  },
  users: {
    all: ['users'] as const,
    list: (q: ListUsersQuery) => [...queryKeys.users.all, 'list', q] as const,
    byIds: (ids: readonly string[]) => [...queryKeys.users.all, 'byIds', [...ids].sort()] as const,
    detail: (id: string) => [...queryKeys.users.all, 'detail', id] as const,
    directReports: (id: string) => [...queryKeys.users.all, 'directReports', id] as const,
    versions: (id: string) => [...queryKeys.users.all, 'versions', id] as const,
    me: () => [...queryKeys.users.all, 'me'] as const,
    submittableByMe: (q: { fromDate: string; toDate: string }) =>
      [...queryKeys.users.all, 'submittableByMe', q] as const,
    // Distinct namespace from `list` — same-shaped params must not collide
    // with the plain employee list's cache entries.
    srControllerCandidates: (q: ListSrControllerCandidatesQuery) =>
      [...queryKeys.users.all, 'srControllerCandidates', q] as const,
  },
  userEmployeeCosts: {
    all: ['userEmployeeCosts'] as const,
    history: (userId: string) => [...queryKeys.userEmployeeCosts.all, 'history', userId] as const,
    inEffect: (userId: string, asOf?: string) =>
      [...queryKeys.userEmployeeCosts.all, 'inEffect', userId, asOf ?? 'today'] as const,
  },
  // The caller's OWN full-catalog-with-ratings overlay ("My Skills"), kept
  // out of `users` for its own invalidation lifecycle (same split as
  // userEmployeeCosts vs. users above).
  userSkills: {
    all: ['userSkills'] as const,
    mine: () => [...queryKeys.userSkills.all, 'mine'] as const,
    // Someone else's ratings, read-only (manager viewing a report).
    of: (userId: string) => [...queryKeys.userSkills.all, 'of', userId] as const,
  },
  skillSubmissions: {
    all: ['skillSubmissions'] as const,
    list: (q: ListSkillSubmissionsQuery) => [...queryKeys.skillSubmissions.all, 'list', q] as const,
  },
  timesheets: {
    all: ['timesheets'] as const,
    list: (q: ListTimesheetsQuery) => [...queryKeys.timesheets.all, 'list', q] as const,
    range: (q: RangeQuery) => [...queryKeys.timesheets.all, 'range', q] as const,
    detail: (id: string) => [...queryKeys.timesheets.all, 'detail', id] as const,
    rejection: (id: string) => [...queryKeys.timesheets.all, 'rejection', id] as const,
    inScopeActivities: (q: { userId: string; fromDate: string; toDate: string }) =>
      [...queryKeys.timesheets.all, 'inScopeActivities', q] as const,
    recentActivities: (q: { userId: string; fromDate: string; toDate: string }) =>
      [...queryKeys.timesheets.all, 'recentActivities', q] as const,
    engagements: (q: { userId: string; fromDate: string; toDate: string }) =>
      [...queryKeys.timesheets.all, 'engagements', q] as const,
  },
  timers: {
    all: ['timers'] as const,
    open: () => [...queryKeys.timers.all, 'open'] as const,
    detail: (id: string) => [...queryKeys.timers.all, 'detail', id] as const,
    byStatus: (s: TimerStatus) => [...queryKeys.timers.all, 'byStatus', s] as const,
  },
  audit: {
    all: ['audit'] as const,
    forEntity: (q: ListAuditQuery) => [...queryKeys.audit.all, 'entity', q] as const,
    list: (q: ListAuditQuery) => [...queryKeys.audit.all, 'list', q] as const,
  },
  approvalPolicies: {
    all: ['approvalPolicies'] as const,
    list: (q: ListApprovalPoliciesQuery) => [...queryKeys.approvalPolicies.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.approvalPolicies.all, 'detail', id] as const,
  },
  approvalInstances: {
    all: ['approvalInstances'] as const,
    forObject: (objectType: string, objectId: string) =>
      [...queryKeys.approvalInstances.all, 'forObject', objectType, objectId] as const,
  },
  timesheetApprovals: {
    all: ['timesheetApprovals'] as const,
    pending: (q: {
      weekStartDate?: string;
      weekEndDate?: string;
      customerIds?: readonly string[];
      userIds?: readonly string[];
      groupBy?: 'employee' | 'customer';
      groupLimit?: number;
    }) =>
      [
        ...queryKeys.timesheetApprovals.all,
        'pending',
        {
          weekStartDate: q.weekStartDate ?? null,
          weekEndDate: q.weekEndDate ?? null,
          customerIds: q.customerIds ? [...q.customerIds].sort() : undefined,
          userIds: q.userIds ? [...q.userIds].sort() : undefined,
          groupBy: q.groupBy ?? null,
          groupLimit: q.groupLimit ?? null,
        },
      ] as const,
    count: () => [...queryKeys.timesheetApprovals.all, 'count'] as const,
    budgetSignals: (q: {
      weekStartDate?: string;
      weekEndDate?: string;
      customerIds?: readonly string[];
      userIds?: readonly string[];
    }) =>
      [
        ...queryKeys.timesheetApprovals.all,
        'budgetSignals',
        {
          weekStartDate: q.weekStartDate ?? null,
          weekEndDate: q.weekEndDate ?? null,
          customerIds: q.customerIds ? [...q.customerIds].sort() : undefined,
          userIds: q.userIds ? [...q.userIds].sort() : undefined,
        },
      ] as const,
    approvedSearch: (q: {
      fromDate?: string;
      toDate?: string;
      customerIds?: readonly string[];
      userIds?: readonly string[];
      limit?: number;
      offset?: number;
    }) =>
      [
        ...queryKeys.timesheetApprovals.all,
        'approvedSearch',
        {
          fromDate: q.fromDate,
          toDate: q.toDate,
          customerIds: q.customerIds ? [...q.customerIds].sort() : undefined,
          userIds: q.userIds ? [...q.userIds].sort() : undefined,
          limit: q.limit,
          offset: q.offset,
        },
      ] as const,
  },
  skillRatingApprovals: {
    all: ['skillRatingApprovals'] as const,
    pending: () => [...queryKeys.skillRatingApprovals.all, 'pending'] as const,
    count: () => [...queryKeys.skillRatingApprovals.all, 'count'] as const,
  },
  timesheetSettings: {
    all: ['timesheetSettings'] as const,
    current: () => [...queryKeys.timesheetSettings.all, 'current'] as const,
  },
  timesheetUnlocks: {
    all: ['timesheetUnlocks'] as const,
    list: (q: ListTimesheetUnlocksQuery) => [...queryKeys.timesheetUnlocks.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.timesheetUnlocks.all, 'detail', id] as const,
    // Active unlocks covering a given user's week — drives the grid hint.
    activeForUser: (q: { userId: string; fromDate: string; toDate: string }) =>
      [...queryKeys.timesheetUnlocks.all, 'activeForUser', q] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: (q: ListNotificationsQuery) => [...queryKeys.notifications.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.notifications.all, 'detail', id] as const,
    preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
    preference: (templateName: string) =>
      [...queryKeys.notifications.all, 'preference', templateName] as const,
    templates: () => [...queryKeys.notifications.all, 'templates'] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
  },
  /**
   * The custom-report engine: the vocabulary, and the members of one dimension.
   *
   * Separate from `reportDefinitions`, which holds the SAVED reports. This is
   * what the builder reads to know what can be asked; that is what somebody
   * asked and kept.
   */
  analytics: {
    all: ['analytics'] as const,
    models: () => [...queryKeys.analytics.all, 'models'] as const,
    /** One page of a filter's option list. Keyed on the period as well as the
     *  search, because the members ARE the values that period contains. */
    dimensionValues: (
      model: string,
      dimension: string,
      q: { from: string; to: string; q?: string; limit: number },
    ) => [...queryKeys.analytics.all, 'members', model, dimension, q] as const,
    /** One member, resolved from its key — what turns a saved report's stored
     *  uuid back into the name its chip shows. */
    dimensionValue: (
      model: string,
      dimension: string,
      q: { from: string; to: string; key: string },
    ) => [...queryKeys.analytics.all, 'member', model, dimension, q] as const,
    /** One `/api/analytics/query` run, keyed on the whole spec — a page that
     *  runs a fixed spec (not the builder, which tracks its draft separately)
     *  caches and re-fetches by the same rule any other query here does. */
    query: (spec: ReportSpec) => [...queryKeys.analytics.all, 'query', spec] as const,
  },
  /** Saved custom reports — the DEFINITIONS, not their rows. */
  reportDefinitions: {
    all: ['report-definitions'] as const,
    list: (q: ListSavedReportsQuery) => [...queryKeys.reportDefinitions.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.reportDefinitions.all, 'detail', id] as const,
    /** Every saved report a drill menu may consider — see `useReportDrill`. */
    drillTargets: () => [...queryKeys.reportDefinitions.all, 'drill-targets'] as const,
  },
  /** Labels on the Reports page, and which reports carry them — one entry. */
  reportLabels: {
    all: ['report-labels'] as const,
  },
  dashboards: {
    all: ['dashboards'] as const,
    list: (q: ListDashboardsQuery) => [...queryKeys.dashboards.all, 'list', q] as const,
    detail: (id: string) => [...queryKeys.dashboards.all, 'detail', id] as const,
    /**
     * One tile's data.
     *
     * Keyed on the PLAN, not on the tile: `planKey` covers the model, the
     * group-bys, the measures, the filters and the window, and deliberately
     * excludes presentation — so switching a tile from a table to a bar, or
     * toggling a total, reuses the answer instead of re-running the aggregate.
     *
     * The consequence worth having is the other way round: two tiles asking
     * the same question — the same report placed twice, or two dashboards
     * sharing one — collapse to a single request, because neither the
     * dashboard id nor the tile id is in the key.
     */
    tile: (planKey: string) => [...queryKeys.dashboards.all, 'tile', planKey] as const,
  },
  reports: {
    all: ['reports'] as const,
    timesheetColumns: () => [...queryKeys.reports.all, 'timesheet', 'columns'] as const,
    timesheet: (q: TimesheetReportQuery) =>
      [
        ...queryKeys.reports.all,
        'timesheet',
        'list',
        { ...q, status: q.status ? [...q.status].sort() : undefined },
      ] as const,
  },
  session: ['session'] as const,
};
